/**
=========================================================
* Material Kit 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useContext } from "react";

import { Routes, Route, useLocation } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Global, css } from "@emotion/react";

import theme from "assets/theme";
import Home from "pages/Home/index";

import routes from "routes";
import footerRoutes from "./footer.routes";

import NavbarTransparent from "components/CBNavigation";
import Term from "pages/Terms";
import PrivacyPolicy from "pages/PrivacyPolicy";

import TherapistBio from "pages/TherapistBio";
import EducationalCourses from "pages/EducationalCourses";
import MetaPixel from "components/CBMetaPixel";
import pixelConfig from "./components/CBMetaPixel/metaPixel.config.data.json";
import usePageTracking from "./utils/hooks/usePageTracking.hooks";
import useGHLTracking from "./utils/hooks/useGHLTracking";
import AuthPage from "pages/Auth";
import Shop from "pages/Shop";
import ProductPage from "pages/ProductPage";
import Checkout from "pages/Checkout";

import { AuthContext } from "utils/context/authentication.context";
import { UserContext } from "utils/context/user.context";
import OrderConfirmation from "pages/orderConfirmation";

import Page404 from "pages/404";
import Footer from "./components/CBFooter";
import Contact from "pages/ContactPage";
import Candidate from "pages/Candidate";
import Careers from "pages/Careers";
import Blog from "pages/Blog";
import BlogPost from "pages/Blog/BlogPost";
import RefundPage from "pages/RefundPage";
import PartnerProspectSurveyPage from "pages/PartnerProspectSurvey";
import InsuranceOptionsHub from "pages/InsuranceOptionsHub";
import InformedConsentPage from "pages/InformedConsent";
import TAMetaOGTags from "components/TAMetaOGTags";
import ConsumerHealthDataPrivacyPolicy from "pages/ConsumerHealthDataPrivacyPolicy";
import ResetPassword from "pages/ResetPassword";
import OurProgram from "pages/OurProgram";
import WhatIsKetamine from "pages/WhatIsKetamine";
import ScienceAndResearch from "pages/ScienceAndResearch";
import PlanningAhead from "pages/PlanningAhead";
import SessionPreparation from "pages/SessionPreparation";
import CreatingYourSpace from "pages/CreatingYourSpace";
import MusicAndTherapy from "pages/MusicAndTherapy";
import Chaperone from "pages/Chaperone";
import TherapyState from "pages/TherapyState";
import TherapyStateFaq from "pages/TherapyStateFaq";
import TherapyStateProviders from "pages/TherapyStateProviders";
import StateNotFound from "pages/StateNotFound";
import Cart from "./pages/Shop/components/Cart";
import useFetchProducts from "pages/Shop/hooks/useFetchProducts";
import TherapyType from "pages/TherapyType";
import TreatmentHub from "pages/TreatmentHub";
import TreatmentDetails from "pages/TreatmentDetails";
import OnlineAssessmentHub from "pages/OnlineAssessmentHub";
import OnlineAssessment from "pages/OnlineAssessment";
import InsuranceOptionsDetails from "pages/InsuranceOptionsDetails";
import InsuranceCheckCoverage from "pages/InsuranceCheckCoverage";
// import InsuranceAddMore from "pages/InsuranceAddMore";
import InsuranceOptionsDynamic from "pages/InsuranceOptionsDynamic";
import TherapyHub from "pages/TherapyHub";

export default function App() {
  const { pathname } = useLocation();
  const { isAuthorized } = useContext(AuthContext);
  const { currentUserProfile } = useContext(UserContext);
  const { products, addons } = useFetchProducts();
  // GA4 - page tracking
  usePageTracking();

  // GHL Dynamic Call Tracking
  useGHLTracking({
    locationId: "pTpZs5Up0T2OAJPqtcLs",
    poolId: "N25vpDUezjC9jBRSwq5x",
    type: "pool",
    maxRetries: 5,
    retryDelay: 500,
    debug: process.env.NODE_ENV === "development",
  });

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // const getRoutes = (allRoutes, isAuthorized, role) => {
  //   return allRoutes.flatMap((route) => {
  //     if (route.collapse) {
  //       return getRoutes(route.collapse, isAuthorized, role);
  //     }

  //     if (route.route) {
  //       // Check for authorization and role
  //       if (route.protected && !isAuthorized) {
  //         return [];
  //       }
  //       if (route.requiredRole && role !== route.requiredRole) {
  //         return [];
  //       }
  //       return [<Route exact path={route.route} element={route.component} key={route.key} />];
  //     }

  //     return [];
  //   });
  // };
  const getRoutes = (allRoutes, isAuthorized, role) => {
    return allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse, isAuthorized, role);
      }

      if (route.submenu) {
        return getRoutes(route.submenu, isAuthorized, role);
      }

      // Only create route if it has a component/element
      if (route.route && (route.component || route.element)) {
        if (route.protected && !isAuthorized) {
          return [];
        }
        if (route.requiredRole && role !== route.requiredRole) {
          return [];
        }
        const element = route.element || route.component;
        return [<Route exact path={route.route} element={element} key={route.key} />];
      }

      return [];
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Global CSS to hide default reCAPTCHA badge - we use custom branding instead */}
      <Global
        styles={css`
          .grecaptcha-badge {
            visibility: hidden !important;
          }
        `}
      />
      <NavbarTransparent />
      <MetaPixel pixelId={pixelConfig.pixelId} events={pixelConfig.events} />
      <TAMetaOGTags pathname={pathname} />
      {/* <Suspense fallback="Loading"> */}
      <Routes>
        {getRoutes(routes, isAuthorized, currentUserProfile?.role)}
        <Route exact path="/" element={<Home />} />
        <Route path="/contact-us" element={<Contact />} />
        <Route path="/terms" element={<Term />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/candidate" element={<Candidate />} />
        <Route path="/careers" element={<Careers />} />
        {/* Blog routes */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />}></Route>
        {/* Modality based blog routes */}
        <Route path="/:modalityType/blog" element={<Blog />} />
        <Route path="/:modalityType/blog/:slug" element={<BlogPost />}></Route>

        <Route path="/partner-providers/:therapistName" element={<TherapistBio />} />
        <Route path="/refund-policy" element={<RefundPage />} />
        <Route path="/partners/prospect-survey" element={<PartnerProspectSurveyPage />} />
        <Route path="/educational-courses" element={<EducationalCourses />} />

        <Route path="/therapist-auth/sign-up" element={<AuthPage />}></Route>
        <Route path="/therapist-auth/sign-in" element={<AuthPage />}></Route>
        <Route path="/informed-consent" element={<InformedConsentPage />}></Route>
        <Route path="/consumer-health-data-privacy-policy" element={<ConsumerHealthDataPrivacyPolicy />}></Route>
        <Route path="/reset-password" element={<ResetPassword />}></Route>
        {/* Shop routes */}
        <Route path="/ketamine-therapy/packages" element={<Shop />}></Route>
        <Route path="/ketamine-therapy/packages/:productName" element={<ProductPage />}></Route>
        <Route path="/checkout" element={<Checkout />}></Route>
        <Route path="/order-confirmation" element={<OrderConfirmation />}></Route>
        {/* Ketamine therapy routes */}
        <Route path="/ketamine-therapy/our-program" element={<OurProgram />}></Route>
        <Route path="/ketamine-therapy/what-is-ketamine" element={<WhatIsKetamine />}></Route>
        <Route path="/ketamine-therapy/science-and-research" element={<ScienceAndResearch />}></Route>
        <Route path="/ketamine-therapy/planning-ahead" element={<PlanningAhead />}></Route>
        <Route path="/ketamine-therapy/session-preparations" element={<SessionPreparation />}></Route>
        <Route path="/ketamine-therapy/creating-your-space" element={<CreatingYourSpace />}></Route>
        <Route path="/ketamine-therapy/music-and-therapy" element={<MusicAndTherapy />}></Route>
        <Route path="/ketamine-therapy/adult-chaperone" element={<Chaperone />}></Route>
        {/* Ketamine State Specific routes */}
        <Route path="/ketamine-therapy/:state" element={<TherapyState />}></Route>
        <Route path="/ketamine-therapy/:state/faq" element={<TherapyStateFaq />}></Route>
        <Route path="/ketamine-therapy/:state/providers" element={<TherapyStateProviders />}></Route>
        <Route path="/ketamine-therapy/state-not-found" element={<StateNotFound />}></Route>
        {/* Psilocybin state specific routes */}
        <Route path="/psilocybin-therapy/:state" element={<TherapyState />}></Route>
        <Route path="/psilocybin-therapy/:state/faq" element={<TherapyStateFaq />}></Route>
        <Route path="/psilocybin-therapy/:state/providers" element={<TherapyStateProviders />}></Route>
        {/* Online Therapy routes */}
        <Route path="/online-therapy" element={<TherapyHub />}></Route>
        <Route path="/online-therapy/individual-therapy" element={<TherapyType />}></Route>
        <Route path="/online-therapy/individual-therapy/:state" element={<TherapyState />}></Route>
        <Route path="/online-therapy/couples-therapy" element={<TherapyType />}></Route>
        <Route path="/online-therapy/couples-therapy/:state" element={<TherapyState />}></Route>
        <Route path="/online-therapy/veterans" element={<TherapyType />}></Route>
        <Route path="/online-therapy/veterans/:state" element={<TherapyState />}></Route>
        <Route path="/online-therapy/lgbtqia" element={<TherapyType />}></Route>
        <Route path="/online-therapy/lgbtqia/:state" element={<TherapyState />}></Route>
        {/* Treatments routes */}
        <Route path="/treatment" element={<TreatmentHub />}></Route>
        <Route path="/treatment/:treatmentType" element={<TreatmentDetails />}></Route>
        {/* Online Assessment routes */}
        <Route path="/online-assessment" element={<OnlineAssessmentHub />}></Route>
        <Route path="/online-assessment/:assessmentType" element={<OnlineAssessment />}></Route>
        {/* Insurance Options routes */}
        <Route path="/insurance-options" element={<InsuranceOptionsHub />}></Route>
        <Route path="/insurance-options/:insuranceType" element={<InsuranceOptionsDetails />}></Route>
        {/* <Route path="/insurance-options/aetna" element={<InsuranceOptionsDetails />}></Route> */}

        <Route path="/insurance-options/check-coverage" element={<InsuranceCheckCoverage />}></Route>
        <Route path="/ketamine-therapy/insurance-options" element={<InsuranceOptionsDynamic />}></Route>
        {/* 404 Route */}
        <Route path="/online-therapy/insurance-options" element={<Page404 />}></Route>
        <Route path="/psilocybin-therapy/insurance-options" element={<Page404 />}></Route>
        <Route path="*" element={<Page404 />}></Route>
      </Routes>
      <Footer footerRoutes={footerRoutes} />
      <Cart addons={addons} products={products} />

      {/* <Cart /> */}
    </ThemeProvider>
  );
}
